import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { 
  Box, Typography, Card, CardContent, Grid, 
  LinearProgress, Button, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import apiService from '../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const GPUDetail = () => {
  const { gpuId } = useParams();
  const [gpuData, setGpuData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(24); // Default to 24 hours

  useEffect(() => {
    const fetchGpuDetails = async () => {
      setLoading(true);
      try {
        // Fetch the history data
        const historyResponse = await apiService.getGpuHistory(gpuId, timeRange);
        setHistoryData(historyResponse);
        
        // For current data, we can use the first GPU in the node that matches this ID
        const stats = await apiService.getGpuStats();
        
        // Find the GPU with the matching ID by searching through all nodes
        let foundGpu = null;
        let nodeId = null;
        
        for (const node of stats) {
          for (const gpu of node.gpus) {
            if (gpu.gpu_id === parseInt(gpuId)) {
              foundGpu = gpu;
              nodeId = node.node_id;
              break;
            }
          }
          if (foundGpu) break;
        }
        
        if (foundGpu) {
          setGpuData({ ...foundGpu, nodeId });
        } else {
          throw new Error('GPU not found');
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching GPU details:', err);
        setError(`Failed to fetch GPU details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchGpuDetails();
  }, [gpuId, timeRange]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const renderUtilizationChart = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      return <Alert severity="info">No historical data available</Alert>;
    }

    const chartData = {
      labels: historyData.history.map(entry => {
        const date = new Date(entry.timestamp);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'GPU Utilization %',
          data: historyData.history.map(entry => entry.avg_utilization || entry.utilization),
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `GPU ${historyData.gpu_index} Utilization History`,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Utilization %'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const renderMemoryChart = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      return <Alert severity="info">No historical data available</Alert>;
    }

    const chartData = {
      labels: historyData.history.map(entry => {
        const date = new Date(entry.timestamp);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'Memory Usage (MB)',
          data: historyData.history.map(entry => entry.avg_memory_usage || entry.memory_usage),
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `GPU ${historyData.gpu_index} Memory Usage History`,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Memory (MB)'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  const renderTemperatureChart = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      return <Alert severity="info">No historical temperature data available</Alert>;
    }

    // Check if temperature data exists
    const hasTemperatureData = historyData.history.some(
      entry => (entry.avg_temperature || entry.temperature) != null
    );

    if (!hasTemperatureData) {
      return <Alert severity="info">Temperature data not available</Alert>;
    }

    const chartData = {
      labels: historyData.history.map(entry => {
        const date = new Date(entry.timestamp);
        return date.toLocaleTimeString();
      }),
      datasets: [
        {
          label: 'Temperature (°C)',
          data: historyData.history.map(entry => entry.avg_temperature || entry.temperature),
          borderColor: 'rgba(255, 159, 64, 1)',
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          tension: 0.4,
          fill: true,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `GPU ${historyData.gpu_index} Temperature History`,
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          title: {
            display: true,
            text: 'Temperature (°C)'
          }
        },
        x: {
          title: {
            display: true,
            text: 'Time'
          }
        }
      }
    };

    return <Line data={chartData} options={options} />;
  };

  if (loading && !gpuData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !gpuData) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!gpuData) {
    return <Alert severity="error">GPU data not found</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            component={RouterLink} 
            to="/" 
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            GPU {gpuData.gpu_index} Details
          </Typography>
        </Box>
        <Button 
          component={RouterLink} 
          to={`/node/${gpuData.nodeId}`}
          variant="outlined"
        >
          View Node: {gpuData.nodeId}
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Current Utilization</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">
                  {gpuData.utilization}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={gpuData.utilization} 
                color={
                  gpuData.utilization < 30 ? "success" : 
                  gpuData.utilization < 70 ? "warning" : "error"
                }
                sx={{ height: 10, borderRadius: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Memory Usage</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">
                  {gpuData.memory_usage} MB
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(gpuData.memory_usage / 32768) * 100} 
                color="info"
                sx={{ height: 10, borderRadius: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Historical Data
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            id="time-range-select"
            value={timeRange}
            label="Time Range"
            onChange={handleTimeRangeChange}
          >
            <MenuItem value={1}>Last Hour</MenuItem>
            <MenuItem value={6}>Last 6 Hours</MenuItem>
            <MenuItem value={24}>Last 24 Hours</MenuItem>
            <MenuItem value={72}>Last 3 Days</MenuItem>
            <MenuItem value={168}>Last Week</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderUtilizationChart()
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderMemoryChart()
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Card>
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                renderTemperatureChart()
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GPUDetail; 